import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PageWrapper from "../pageWrapper";

//CSS
import "../0PageStyles.css";
import "./ReportPage.css";

//Components
import PageLoader from "../../Components/Other Components/pageLoader";
import Title from "../../Components/Other Components/Title";

//Functions and hooks
import { GetInventoryReport } from "../../BackendAccess/DataHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import { formatCurrency, useSettings } from "../../Contexts/SettingsContext";

export default function ReportPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pageError, setPageError] = useState(null);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);

  const { companySettings } = useSettings();
  const { t } = useTranslation("items");

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const [data, error] = await GetInventoryReport();
      if (error) {
        error.isPageError = true;
        setError(error);
      } else {
        setReport(data);
      }
    } finally {
      setLoading(false);
    }
  };

  const noteTypeLabel = (type) => t(type, { ns: "common" });
  const currency = (value) => formatCurrency(value, companySettings.currency);

  const tutorialSteps = [
    {
      target: null,
      title: t("Inventory report", { ns: "tutorial" }),
      message: t(
        "This page summarizes your entire inventory - total stock value, unit and item counts, low-stock warnings, note activity, and your top sellers.",
        { ns: "tutorial" }
      ),
    },
    {
      target: ".report-stat-grid",
      title: t("Overview stats", { ns: "tutorial" }),
      message: t(
        "Your total stock value at both purchase and selling price, total units and distinct items in stock, and how many items are currently below their minimum stock.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "report-note-counts",
      title: t("Notes created, by type", { ns: "tutorial" }),
      message: t(
        "How many received goods, delivery, and sale notes you've created in total.",
        { ns: "tutorial" }
      ),
    },
    {
      target: "report-top-selling",
      title: t("Top selling items", { ns: "tutorial" }),
      message: t(
        "Your five best-selling items by quantity, based on Sale-type traffic notes, along with the revenue each has brought in.",
        { ns: "tutorial" }
      ),
    },
  ];

  return (
    <PageWrapper tutorialId="report" tutorialSteps={tutorialSteps}>
      <PageLoader loading={loading} error={pageError}>
        <div className="page-content">
          <Title className="mt-3">{t("Report")}</Title>
          {report && (
            <>
              <div className="report-stat-grid w-90">
                <div className="container-bordered report-stat-card">
                  <span className="report-stat-label">
                    {t("Stock value (purchase)")}
                  </span>
                  <span className="report-stat-value">
                    {currency(report.TotalPurchaseValue)}
                  </span>
                </div>
                <div className="container-bordered report-stat-card">
                  <span className="report-stat-label">
                    {t("Stock value (selling)")}
                  </span>
                  <span className="report-stat-value">
                    {currency(report.TotalSellingValue)}
                  </span>
                </div>
                <div className="container-bordered report-stat-card">
                  <span className="report-stat-label">{t("Total units in stock")}</span>
                  <span className="report-stat-value">{report.TotalUnits}</span>
                </div>
                <div className="container-bordered report-stat-card">
                  <span className="report-stat-label">{t("Distinct items")}</span>
                  <span className="report-stat-value">{report.TotalItems}</span>
                </div>
                <div className="container-bordered report-stat-card">
                  <span className="report-stat-label">{t("Low Stock")}</span>
                  <span className="report-stat-value">
                    {report.LowStockCount}
                  </span>
                </div>
              </div>

              <div className="container-bordered w-90 mt-4 vertical" data-tutorial-id="report-note-counts">
                <Title className="font-m mb-2">
                  {t("Notes created, by type")}
                </Title>
                {report.NoteCounts.length === 0 ? (
                  <p className="cardPlaceholder">{t("No notes recorded yet.")}</p>
                ) : (
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>{t("Note type")}</th>
                        <th>{t("Count")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.NoteCounts.map((row) => (
                        <tr key={row.Type}>
                          <td>{noteTypeLabel(row.Type)}</td>
                          <td>{row.Count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="container-bordered w-90 mt-4 mb-5 vertical" data-tutorial-id="report-top-selling">
                <Title className="font-m mb-2">{t("Top selling items")}</Title>
                {report.TopSellingItems.length === 0 ? (
                  <p className="cardPlaceholder">
                    {t("No sales recorded yet.")}
                  </p>
                ) : (
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>{t("ID")}</th>
                        <th>{t("Name")}</th>
                        <th>{t("Units sold")}</th>
                        <th>{t("Revenue")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.TopSellingItems.map((row) => (
                        <tr key={row.ItemID}>
                          <td>{row.ItemID}</td>
                          <td>{row.Name}</td>
                          <td>{row.TotalSold}</td>
                          <td>{currency(row.TotalRevenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </div>
      </PageLoader>
    </PageWrapper>
  );
}
